'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@kit/ui/dialog';
import { toast } from '@kit/ui/sonner';
import { Textarea } from '@kit/ui/textarea';

import { adminStartSupportConversationForUserAction } from '../server/support-actions';

interface MessageAsBalleeSupportDialogProps {
  /** The user to message (already known on the participants/entries panels). */
  targetUserId: string;
  /** Display name for the target, shown in the dialog copy. */
  targetName?: string | null;
  /** The clickable trigger (e.g. a Button). */
  children: React.ReactNode;
  /** Fires after a successful send (e.g. to toast/refresh the caller). */
  onSent?: (conversationId: string) => void;
}

const MAX_LEN = 4000;

/**
 * Super-admin / Ballee-org action: message a specific user as "Ballee Support".
 * The target can be private, non-public, or archived — there is no privacy gate
 * (authorization is enforced server-side in the action's RPC). Reused verbatim
 * across every surface that already knows the target user (participants panel,
 * entries panel); the support-inbox composer wraps this with a user picker.
 */
export function MessageAsBalleeSupportDialog({
  targetUserId,
  targetName,
  children,
  onSent,
}: MessageAsBalleeSupportDialogProps) {
  const { t } = useTranslation('chat');
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const trimmed = body.trim();
  const canSend = trimmed.length > 0 && trimmed.length <= MAX_LEN && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const result = await adminStartSupportConversationForUserAction({
        targetUserId,
        body: trimmed,
      });
      toast.success(t('adminSupport.sentToast'));
      setBody('');
      setOpen(false);
      onSent?.(result.conversationId);
    } catch {
      toast.error(t('adminSupport.errorToast'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('adminSupport.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {targetName
              ? t('adminSupport.dialogDescriptionNamed', { name: targetName })
              : t('adminSupport.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <Textarea
          autoFocus
          rows={5}
          maxLength={MAX_LEN}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('adminSupport.placeholder')}
          disabled={sending}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={sending}
          >
            {t('adminSupport.cancel')}
          </Button>
          <Button type="button" onClick={handleSend} disabled={!canSend}>
            {sending ? t('adminSupport.sending') : t('adminSupport.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
